package com.tovtalk.presentation.viewmodel.comments

import com.tovtalk.domain.entity.Comment
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.ReplyComment
import com.tovtalk.domain.repository.CommentRepository
import com.tovtalk.presentation.state.PagingState
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.cancel

import com.tovtalk.presentation.ViewModel
import com.tovtalk.presentation.ScopedViewModel

/**
 * Comments ViewModel - מסך תגובות
 */
class CommentsViewModel(
    private val commentRepository: CommentRepository,
    private val scope: CoroutineScope
) : ViewModel(), ScopedViewModel {

    /** Cancels the injected scope + all launched jobs when the sheet/panel leaves. */
    override fun dispose() {
        scope.cancel()
    }


    private val _commentsState = MutableStateFlow(PagingState<Comment>())
    val commentsState: StateFlow<PagingState<Comment>> = _commentsState.asStateFlow()

    private val _addCommentState = MutableStateFlow<UiState<Comment>>(UiState.Idle)
    val addCommentState: StateFlow<UiState<Comment>> = _addCommentState.asStateFlow()

    private val _replyingTo = MutableStateFlow<Comment?>(null)
    val replyingTo: StateFlow<Comment?> = _replyingTo.asStateFlow()

    private val _expandedReplies = MutableStateFlow<Set<Int>>(emptySet())
    val expandedReplies: StateFlow<Set<Int>> = _expandedReplies.asStateFlow()

    private val _repliesMap = MutableStateFlow<Map<Int, List<ReplyComment>>>(emptyMap())
    val repliesMap: StateFlow<Map<Int, List<ReplyComment>>> = _repliesMap.asStateFlow()

    private var currentPostId: Int = 0
    private var currentPage: Int = 1
    private val replyPages = mutableMapOf<Int, Int>()

    /**
     * טעינת תגובות לפוסט
     */
    fun loadComments(postId: Int, refresh: Boolean = false) {
        if (_commentsState.value.isLoading) return

        currentPostId = postId

        scope.launch {
            if (refresh) {
                currentPage = 1
                _commentsState.value = PagingState(isLoading = true)
            } else {
                _commentsState.value = _commentsState.value.copy(isLoading = true)
            }

            when (val result = commentRepository.getPostComments(postId, currentPage)) {
                is DomainResult.Success -> {
                    val currentItems = if (refresh) emptyList() else _commentsState.value.items
                    val merged = (currentItems + result.data).distinctBy { it.id }
                    currentPage++
                    _commentsState.value = PagingState(
                        items = merged,
                        isLoading = false,
                        hasMore = result.data.size >= 20
                    )
                }
                is DomainResult.Error -> {
                    _commentsState.value = _commentsState.value.copy(
                        isLoading = false,
                        hasMore = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {
                    _commentsState.value = _commentsState.value.copy(isLoading = true)
                }
            }
        }
    }

    /**
     * טעינת עוד תגובות
     */
    fun loadMore() {
        if (_commentsState.value.isLoadingMore || !_commentsState.value.hasMore || _commentsState.value.isLoading) return

        scope.launch {
            _commentsState.value = _commentsState.value.copy(isLoadingMore = true)

            when (val result = commentRepository.getPostComments(currentPostId, currentPage)) {
                is DomainResult.Success -> {
                    val merged = (_commentsState.value.items + result.data).distinctBy { it.id }
                    currentPage++
                    _commentsState.value = PagingState(
                        items = merged,
                        isLoadingMore = false,
                        hasMore = result.data.size >= 20
                    )
                }
                is DomainResult.Error -> {
                    _commentsState.value = _commentsState.value.copy(
                        isLoadingMore = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {
                    _commentsState.value = _commentsState.value.copy(isLoadingMore = true)
                }
            }
        }
    }

    /**
     * הוספת תגובה חדשה
     */
    fun addComment(text: String) {
        if (text.isBlank()) return

        scope.launch {
            _addCommentState.value = UiState.Loading

            when (val result = commentRepository.addComment(currentPostId, text, null)) {
                is DomainResult.Success -> {
                    _addCommentState.value = UiState.Success(result.data)
                    val updatedItems = (listOf(result.data) + _commentsState.value.items).distinctBy { it.id }
                    _commentsState.value = _commentsState.value.copy(items = updatedItems)
                }
                is DomainResult.Error -> {
                    _addCommentState.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {
                    _addCommentState.value = UiState.Loading
                }
            }
        }
    }

    /**
     * מחיקת תגובה
     */
    fun deleteComment(commentId: Int) {
        scope.launch {
            when (val result = commentRepository.deleteComment(commentId)) {
                is DomainResult.Success -> {
                    val updatedItems = _commentsState.value.items.filter { it.id != commentId }
                    _commentsState.value = _commentsState.value.copy(items = updatedItems)
                }
                is DomainResult.Error -> {
                    _commentsState.value = _commentsState.value.copy(error = result.message)
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    /**
     * לייק/אנלייק תגובה
     */
    fun toggleLikeComment(commentId: Int, isLiked: Boolean) {
        scope.launch {
            val result = if (isLiked) {
                commentRepository.unlikeComment(commentId)
            } else {
                commentRepository.likeComment(commentId)
            }

            when (result) {
                is DomainResult.Success -> {
                    val updatedItems = _commentsState.value.items.map { comment ->
                        if (comment.id == commentId) {
                            comment.copy(
                                isLiked = !isLiked,
                                likes = if (isLiked) comment.likes - 1 else comment.likes + 1
                            )
                        } else {
                            comment
                        }
                    }
                    _commentsState.value = _commentsState.value.copy(items = updatedItems)
                }
                is DomainResult.Error -> {}
                is DomainResult.Loading -> {}
            }
        }
    }

    /**
     * התחלת תגובה לתגובה
     */
    fun startReply(comment: Comment) {
        _replyingTo.value = comment
    }

    /**
     * ביטול תגובה לתגובה
     */
    fun cancelReply() {
        _replyingTo.value = null
    }

    /**
     * שליחת תגובה - תומך גם בתגובה רגילה וגם בתגובה לתגובה
     */
    fun submitComment(text: String) {
        val replyTarget = _replyingTo.value
        if (replyTarget != null) {
            addReplyToComment(replyTarget.id, text)
        } else {
            addComment(text)
        }
    }

    /**
     * הוספת תגובה לתגובה קיימת
     */
    private fun addReplyToComment(commentId: Int, text: String) {
        _addCommentState.value = UiState.Loading
        scope.launch {
            when (val result = commentRepository.addReply(commentId, text, null)) {
                is DomainResult.Success -> {
                    val currentReplies = _repliesMap.value[commentId] ?: emptyList()
                    _repliesMap.value = _repliesMap.value + (commentId to (currentReplies + result.data))
                    val updatedItems = _commentsState.value.items.map {
                        if (it.id == commentId) it.copy(replyCount = it.replyCount + 1) else it
                    }
                    _commentsState.value = _commentsState.value.copy(items = updatedItems)
                    _addCommentState.value = UiState.Idle
                    _replyingTo.value = null
                }
                is DomainResult.Error -> {
                    _addCommentState.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    /**
     * הרחבה/צמצום תגובות לתגובה
     */
    fun toggleReplies(commentId: Int) {
        val current = _expandedReplies.value
        if (commentId in current) {
            _expandedReplies.value = current - commentId
        } else {
            _expandedReplies.value = current + commentId
            if (_repliesMap.value[commentId] == null) {
                loadReplies(commentId)
            }
        }
    }

    /**
     * טעינת תגובות לתגובה
     */
    fun loadReplies(commentId: Int) {
        val page = replyPages[commentId] ?: 1
        scope.launch {
            when (val result = commentRepository.getCommentReplies(commentId, page)) {
                is DomainResult.Success -> {
                    val existing = _repliesMap.value[commentId] ?: emptyList()
                    val merged = (existing + result.data).distinctBy { it.id }
                    _repliesMap.value = _repliesMap.value + (commentId to merged)
                    replyPages[commentId] = page + 1
                }
                is DomainResult.Error -> {}
                is DomainResult.Loading -> {}
            }
        }
    }

    /**
     * טעינת עוד תגובות לתגובה
     */
    fun loadMoreReplies(commentId: Int) {
        loadReplies(commentId)
    }

    /**
     * לייק/אנלייק תגובה לתגובה
     */
    fun toggleLikeReply(replyId: Int, commentId: Int, isLiked: Boolean) {
        val currentReplies = _repliesMap.value[commentId] ?: return
        val updatedReplies = currentReplies.map {
            if (it.id == replyId) it.copy(
                isLiked = !isLiked,
                likes = if (isLiked) it.likes - 1 else it.likes + 1
            ) else it
        }
        _repliesMap.value = _repliesMap.value + (commentId to updatedReplies)

        scope.launch {
            val result = if (isLiked) {
                commentRepository.unlikeReply(replyId)
            } else {
                commentRepository.likeReply(replyId)
            }
            when (result) {
                is DomainResult.Error -> {
                    _repliesMap.value = _repliesMap.value + (commentId to currentReplies)
                }
                else -> {}
            }
        }
    }

    /**
     * איפוס מצב הוספת תגובה
     */
    fun resetAddCommentState() {
        _addCommentState.value = UiState.Idle
    }

    /**
     * ניקוי שגיאות
     */
    fun clearError() {
        _commentsState.value = _commentsState.value.copy(error = null)
    }
}
